﻿import { action, computed, makeObservable, observable, reaction } from "mobx";
import { IBathymetryState } from "../models/ChartModels";
import { IColorScale } from "../models/ColorScale";
import { colorScales as colorScalesModels, ColorScaleSpaceIds } from "../models/ColorScales";
import { IStateStore, StateStore } from "./StateStore";


export class BathymetryStore extends StateStore<IBathymetryState> implements IBathymetryState, IStateStore<IBathymetryState> {

    private _cachedColorScales: { [key: number]: IColorScale[] } = {};

    public colorScales: IColorScale[] = colorScalesModels;    
    public colorScaleLegend: boolean | undefined = undefined;
    public spaceId: number | undefined = ColorScaleSpaceIds.ECDIS;
    public depthShallow: number = 0;
    public depthDeep: number = -20;
    public hillShading: boolean = true;
    public visible: boolean = true;
    public terrainExaggeration: number = 3;

    public displayCategories: number[] | undefined = [0, 10];
    public lookupKeys: number[] | undefined = [0];

    constructor(private getColorScalesFn?: () => Promise<IColorScale[]>) {
        super()
        makeObservable(this, {
            visible: observable,
            colorScaleLegend: observable,
            spaceId: observable,
            colorScales: observable.ref,
            depthDeep: observable,
            depthShallow: observable,
            hillShading: observable,
            terrainExaggeration: observable,
            displayCategories: observable.ref,
            setDisplayCategories: action.bound,
            lookupKeys: observable.ref,
            setLookupKeys: action.bound,
            setColorScales: action.bound
        });       

        reaction(() => [this.displayCategories, this.lookupKeys], () => this.setColorScales())
    }     

    public addCachedColorScales(colorScales: IColorScale[]) {
        colorScales.forEach(cs => {
            cs.lookupKeys?.forEach(key => {
                if (!this._cachedColorScales[key])
                    this._cachedColorScales[key] = [cs];
                else if (this._cachedColorScales[key].findIndex(ccs => ccs.spaceId === cs.spaceId) === -1)
                    this._cachedColorScales[key].push(cs);
            })
        });
    }

    public load = async () => {

        if (!this.getColorScalesFn)
            return;

        const colorScales = await this.getColorScalesFn();

        const lookupKeysFromServer = Array.from(new Set(colorScales.map(cs => cs.lookupKeys).flat().filter(key => key !== undefined)));

        this._cachedColorScales = {};
        lookupKeysFromServer?.forEach(key => {
            if (key !== undefined)
                this._cachedColorScales[key] = colorScales.filter(cs => cs.lookupKeys && cs.lookupKeys.indexOf(key) > -1);
        })

        if (this._cachedColorScales[0] === undefined)
            this._cachedColorScales[0] = [];

        this._cachedColorScales[0] = colorScalesModels.filter(cs => cs.spaceId === -1 || cs.spaceId === -2).concat(this._cachedColorScales[0]);

        this.setColorScales();
    }

    public setDisplayCategories(displayCategories: number[]) {
        this.displayCategories = displayCategories;
    }

    public setLookupKeys(lookupKeys: number[]) {
        this.lookupKeys = lookupKeys;
    }

    public setColorScales() {
        if (this.displayCategories === undefined || this.lookupKeys === undefined)
            return;

        if (this.getColorScalesFn) {
            const newColorScales: IColorScale[] = [];
            this.lookupKeys.forEach(key => {

                this._cachedColorScales[key]?.forEach(ccs => {
                    if (this.displayCategories!.findIndex(dc => dc === ccs.displayCategory) > -1 && newColorScales.findIndex(ncs => ncs.spaceId === ccs.spaceId) === -1)
                        newColorScales.push(ccs);
                })

            })

            this.colorScales = newColorScales;
        }
    }

    public deleteColorScales(spaceIds: number[]) {
        this.colorScales = this.colorScales.filter(cs => cs.spaceId != undefined && spaceIds.indexOf(cs.spaceId) == -1);
        Object.keys(this._cachedColorScales).forEach(key => {
            this._cachedColorScales[Number.parseInt(key)] = this._cachedColorScales[Number.parseInt(key)].filter(cs => spaceIds.indexOf(cs.spaceId!) == -1);
        })
    }

    public get getState(): IBathymetryState {
        const { colorScales, colorScaleLegend, depthDeep, depthShallow, hillShading, terrainExaggeration, spaceId } = this;
        return { colorScales, colorScaleLegend, depthDeep, depthShallow, hillShading, terrainExaggeration, spaceId };
    }
}